import { defineStore } from 'pinia'
import { ref, type Ref, watch } from 'vue'

import type {
  Permission,
  Permissions,
  PermissionSet,
} from '@/types/external/2work/api/response/getDoctypePermissions'

import { type DocType, DOCTYPES } from '@/constants/doctypes.ts'
import { i18nextInstance } from '@/plugins/vuePluginI18next'
import {
  useFrappeAuthAPIStore,
  type UserAuthRequest,
  type UserAuthResponse,
} from '@/stores/apis/frappeAuthAPI'
import { useFrappeDataAPIStore } from '@/stores/apis/frappeDataAPI'
import {
  type HasDocumentPermissionResponse,
  useFrappeNestCustomAPIStore,
} from '@/stores/apis/frappeNestCustomAPI'
import { useFrappeSsoAPIStore } from '@/stores/apis/frappeSsoAPI'
import { useThemeStore } from '@/stores/themeStore'
import { handleUnknownError } from '@/utilities/errors'
import { isNetworkErrorRedirectContentPage, NetworkError } from '@/utilities/stores'

type SessionUsername = string | undefined
type RedirectRoutePathAfterLogin = string | undefined

interface SessionCookie {
  full_name: string
  user_id: string
  user_image: string | undefined
}

interface UserRole {
  role: string
}

interface FrappeUserDoc {
  language?: string
  notifications?: 0 | 1
  roles?: UserRole[]
  search_bar?: 0 | 1
  tw_nav_company?: 0 | 1
  tw_nav_help?: 0 | 1
  tw_nav_settings?: 0 | 1
  tw_nav_time_tracking?: 0 | 1
  tw_ui_theme?: string
}

interface NavigationSettings {
  company: boolean
  help: boolean
  notifications: boolean
  searchBar: boolean
  settings: boolean
  timeTracking: boolean
}

const defaultNavigationSettings: NavigationSettings = {
  company: true,
  help: true,
  notifications: true,
  searchBar: true,
  settings: true,
  timeTracking: true,
}

interface NotAccessibleLog {
  doctype: DocType
  permission: Permission
}

const useAuthStore = defineStore('auth', () => {
  const authAPIStore = useFrappeAuthAPIStore()
  const dataAPIStore = useFrappeDataAPIStore()
  const nestCustomAPIStore = useFrappeNestCustomAPIStore()
  const ssoAPIStore = useFrappeSsoAPIStore()
  const themeStore = useThemeStore()

  // State
  const sessionUsername: Ref<SessionUsername> = ref(undefined)
  const sessionCookie = ref<SessionCookie | undefined>(undefined)
  const isAdministrator = ref(false)
  const redirectRoutePathAfterLogin: Ref<RedirectRoutePathAfterLogin> = ref(undefined)
  const isLogoutRedirect: Ref<boolean> = ref(false)
  const accessibleDoctypes = ref<Record<string, Permissions>>({})
  const notAccessibleLog = ref<NotAccessibleLog[]>([])

  const userLanguage = ref<string | undefined>('de')
  const navigationSettings = ref<NavigationSettings>({ ...defaultNavigationSettings })

  const initAccessibleModules = async (): Promise<void> => {
    const requiredDoctypes = Object.values(DOCTYPES)

    try {
      accessibleDoctypes.value = await nestCustomAPIStore.getDoctypePermissions(requiredDoctypes)
    } catch (responseError) {
      console.error('[Access] Failed to load permissions:', responseError)
    }
  }

  // Parse cookie values only (does not call APIs)
  const readSessionCookie = (): void => {
    const sessionCookieValues = document.cookie.split('; ')

    const full_name = sessionCookieValues
      .find((cookie) => cookie.startsWith('full_name='))
      ?.split('=')[1]
    const user_id = sessionCookieValues
      .find((cookie) => cookie.startsWith('user_id='))
      ?.split('=')[1]
    const user_image = sessionCookieValues
      .find((cookie) => cookie.startsWith('user_image='))
      ?.split('=')[1]

    if (full_name && user_id) {
      sessionCookie.value = {
        full_name: decodeURIComponent(full_name),
        user_id: decodeURIComponent(user_id),
        user_image: user_image ? decodeURIComponent(user_image) : undefined,
      }

      console.debug('[Auth] Session cookie parsed:', sessionCookie.value)
    } else {
      console.warn('[Auth] Session cookie not found or invalid')
      sessionCookie.value = undefined
    }
  }

  // Load roles and language for current user via API
  const loadUserData = async (): Promise<void> => {
    const username = sessionCookie.value?.user_id

    try {
      if (!username) {
        console.warn('[Auth] No user name available - role cannot be loaded')
        return
      }
      const userDoc = await dataAPIStore.getDoc<FrappeUserDoc>(`User`, username)
      isAdministrator.value = Array.isArray(userDoc.roles) && userDoc.roles.length > 0
      userLanguage.value = userDoc.language ?? 'de' // Default to 'de' if no language set
      navigationSettings.value = {
        company: userDoc.tw_nav_company !== 0,
        help: userDoc.tw_nav_help !== 0,
        notifications: userDoc.notifications !== 0,
        searchBar: userDoc.search_bar !== 0,
        settings: userDoc.tw_nav_settings !== 0,
        timeTracking: userDoc.tw_nav_time_tracking !== 0,
      }
      themeStore.applyUserPreference(userDoc.tw_ui_theme)
    } catch (catchError) {
      console.error('[Auth] Failed to load User Data:', catchError)
    }
  }

  // Reset all session state
  const userSessionReset = (): void => {
    console.debug('[Auth] Resetting user session')
    sessionUsername.value = undefined
    sessionCookie.value = undefined

    userLanguage.value = undefined // Reset to default language
    isAdministrator.value = false
    navigationSettings.value = { ...defaultNavigationSettings }
    themeStore.resetPreference()
  }

  // Sync session state (used during navigation, app load, etc.)
  const userSessionUpdateState = async (): Promise<void> => {
    const authUsername = await authAPIStore.getAuthUsername().catch((authError: unknown) => {
      const frappeErrorMessagePreText = 'Handle Auth Error:'

      if (authError instanceof NetworkError) {
        if (authError.cause) {
          console.error(
            `${frappeErrorMessagePreText}\n`,
            authError,
            authError.cause.httpErrorCode,
            authError.cause.httpErrorCodeText,
            authError.cause.httpErrorType,
          )
        }
        if (isNetworkErrorRedirectContentPage(authError)) {
          return undefined
        }
      } else if (authError instanceof Error) {
        console.error(frappeErrorMessagePreText, `\nno network error:\n${authError.message}`)
      } else {
        handleUnknownError(authError)
      }

      // is handled in "onError" function from "routerRoot" (redirect error)
      throw authError
    })

    const isExistAuthUsername = Boolean(authUsername)
    const isExistSessionUsername = Boolean(sessionUsername.value)
    const isSameSessionUsername =
      isExistAuthUsername && isExistSessionUsername && authUsername === sessionUsername.value

    if (isExistSessionUsername && !isSameSessionUsername) {
      console.warn('[Auth] Session mismatch — forcing logout')
      userSessionReset()
    }

    if (isExistAuthUsername) {
      sessionUsername.value = authUsername
      readSessionCookie()
      await loadUserData()
    }
  }

  // Check if a valid user session exists
  const isValidUserSession = async (): Promise<boolean> => {
    if (sessionUsername.value) return true

    console.debug('[Auth] No session username — triggering session update')
    await userSessionUpdateState()
    return Boolean(sessionUsername.value)
  }

  // Login and initialize session
  const userSessionLogin = async (userAuthRequest: UserAuthRequest): Promise<UserAuthResponse> => {
    console.debug('[Auth] Attempting login...')
    return await authAPIStore
      .login(userAuthRequest)
      .then(async (authResponse: UserAuthResponse) => {
        // If login successful, authResponse should contain "full_name", otherwise it could need two-factor auth.
        // Only read session cookie and load user data if login is successful
        if (authResponse.full_name) {
          // After login, read the session cookie to get user_id
          readSessionCookie()
          const authUsername = sessionCookie.value?.user_id

          if (authUsername) {
            console.debug('[Auth] Login successful — loading session')
            sessionUsername.value = authUsername
            await loadUserData()
          } else {
            console.warn('[Auth] Login succeeded but authUsername not found in cookie')
          }
        } else if (authResponse.tmp_id && authResponse.verification) {
          console.debug('[Auth] Two-factor authentication required')
        } else {
          throw new Error(`Unexpected login response format: ${JSON.stringify(authResponse)}`)
        }

        return authResponse
      })
      .catch((loginError: unknown) => {
        console.error('[Auth] Login failed:', loginError)
        throw loginError
      })
  }

  // Logout and clear session
  const userSessionLogout = async (): Promise<void> => {
    console.debug('[Auth] Logging out...')

    // Resolve the identity provider logout URL while the session is still valid.
    // Returns null unless Keycloak single logout is enabled on the backend.
    const ssoLogoutUrl = await ssoAPIStore.getLogoutUrl().catch((logoutUrlError: unknown) => {
      console.warn('[Auth] Failed to resolve SSO logout URL:', logoutUrlError)
      return null
    })

    await authAPIStore.logout().then(() => {
      isLogoutRedirect.value = true
      userSessionReset()
    })

    if (ssoLogoutUrl) {
      console.debug('[Auth] Continuing with single logout at the identity provider')
      window.location.assign(ssoLogoutUrl)
    }
  }

  // Utility: clear redirect flag
  const isLogoutRedirectReset = (): void => {
    isLogoutRedirect.value = false
  }

  // Utility: clear redirect path after login
  const redirectRoutePathAfterLoginReset = (): void => {
    redirectRoutePathAfterLogin.value = undefined
  }

  /**
   * Reset the not accessible log.
   * @param withResp
   */
  const resetNotAccessibleLog = (withResp = true): void => {
    if (withResp) {
      notAccessibleLog.value = []
    }
  }

  /**
   * Check if user has permission for a given doctype and permission
   * @param doctype
   * @param permission
   * @param withResp
   * @param resetLog
   * @param if_owner
   */
  const hasPermission = (
    doctype: DocType,
    permission: keyof PermissionSet,
    withResp = false,
    resetLog = false,
    if_owner: boolean | null = null,
  ): boolean => {
    const perms = accessibleDoctypes.value[doctype]
    if (!perms) return false

    if (if_owner === true) return perms.if_owner[permission] === 1
    if (if_owner === false) return perms.normal[permission] === 1

    const res = perms.normal[permission] === 1 || perms.if_owner[permission] === 1

    if (withResp && !res) {
      if (resetLog) resetNotAccessibleLog(withResp)
      notAccessibleLog.value.push({ doctype, permission })
    }

    return res
  }

  /**
   * Check if user has all permissions for a given doctype and permissions array
   * @param doctype
   * @param permissions
   * @param withResp
   * @param if_owner
   */
  const hasPermissions = (
    doctype: DocType,
    permissions: (keyof PermissionSet)[],
    withResp = false,
    if_owner: boolean | null = null,
  ): boolean => {
    resetNotAccessibleLog(withResp)
    return permissions.every((perm) => hasPermission(doctype, perm, withResp, false, if_owner))
  }

  const hasPermissionsAll = (
    doctypes: DocType[],
    permissions: (keyof PermissionSet)[],
    if_owner: boolean | null = null,
    withResp = false,
  ): boolean => {
    return doctypes.every((dt) =>
      permissions.every((perm) => hasPermission(dt, perm, withResp, false, if_owner)),
    )
  }

  /**
   * Check if user has at least one permission for a given doctype and permissions array
   * @param doctype
   * @param permissions
   * @param withResp
   * @param if_owner
   */
  const hasOnePermission = (
    doctype: DocType,
    permissions: (keyof PermissionSet)[],
    withResp = false,
    if_owner: boolean | null = null,
  ): boolean => {
    resetNotAccessibleLog(withResp)
    return permissions.some((perm) => hasPermission(doctype, perm, withResp, false, if_owner))
  }

  /**
   * Check if at least one of the provided doctypes has the given permission.
   * @param doctypes - List of document types to check
   * @param permission - Permission key to check
   * @param withResp
   * @param if_owner
   */
  const hasOneDoctype = (
    doctypes: DocType[],
    permission: keyof PermissionSet,
    withResp = false,
    if_owner: boolean | null = null,
  ): boolean => {
    resetNotAccessibleLog(withResp)
    return doctypes.some((dt) => hasPermission(dt, permission, withResp, false, if_owner))
  }

  /**
   * @param doctypes
   * @param permissions
   * @param withResp
   * @param if_owner
   */
  const permissionsHasOneDoctype = (
    doctypes: DocType[],
    permissions: (keyof PermissionSet)[],
    withResp = false,
    if_owner: boolean | null = null,
  ): boolean => {
    resetNotAccessibleLog(withResp)
    return doctypes.some((dt) =>
      permissions.every((perm) => hasPermission(dt, perm, withResp, false, if_owner)),
    )
  }

  /**
   * Check if all the provided doctypes have the given permission.
   * @param doctypes - List of document types to check
   * @param permission - Permission key to check
   * @param withResp
   * @param if_owner
   */
  const hasDoctypes = (
    doctypes: DocType[],
    permission: keyof PermissionSet,
    withResp = false,
    if_owner: boolean | null = null,
  ): boolean => {
    resetNotAccessibleLog(withResp)
    return doctypes.every((dt) => hasPermission(dt, permission, withResp, false, if_owner))
  }

  const hasDocumentPermission = async (
    doctype: DocType,
    docname: string,
    perm_type: Permission,
  ): Promise<HasDocumentPermissionResponse> => {
    return await nestCustomAPIStore.hasDocumentPermission({
      docname,
      doctype,
      perm_type,
    })
  }

  /**
   * Get the not accessible log.
   */
  const getNotAccessibleLog = (): NotAccessibleLog[] => {
    return notAccessibleLog.value
  }

  /**
   * Get the specific not accessible log for a given doctype.
   * @param index
   */
  const getSpecificNotAccessibleLog = (index: number): NotAccessibleLog | undefined => {
    return notAccessibleLog.value[index]
  }

  watch(
    () => userLanguage.value,
    (newLanguage) => {
      console.debug('[Auth] User language changed:', newLanguage)
      if (newLanguage && ['de', 'en'].includes(newLanguage)) {
        i18nextInstance.changeLanguage(newLanguage).catch((languageerror: unknown) => {
          console.error('[Auth] Failed to change language:', languageerror)
        })
      }
    },
  )

  // Pinia store return
  return {
    // State
    accessibleDoctypes,
    isAdministrator,
    isLogoutRedirect,
    navigationSettings,
    redirectRoutePathAfterLogin,
    sessionCookie,
    sessionUsername,
    userLanguage,

    // Actions
    getNotAccessibleLog,
    getSpecificNotAccessibleLog,
    hasDoctypes,
    hasDocumentPermission,
    hasOneDoctype,
    hasOnePermission,
    hasPermission,
    hasPermissions,
    hasPermissionsAll,
    initAccessibleModules,
    isLogoutRedirectReset,
    isValidUserSession,
    loadUserData,
    permissionsHasOneDoctype,
    redirectRoutePathAfterLoginReset,
    userSessionLogin,
    userSessionLogout,
    userSessionReset,
    userSessionUpdateState,
  }
})

export {
  type RedirectRoutePathAfterLogin,
  type SessionUsername,
  useAuthStore,
  type UserAuthRequest,
  type UserAuthResponse,
}
